import { createContext, useContext, useReducer } from 'react';
import type { ReactElement, ReactNode } from 'react';

import type { IOrderHistoryMenu } from '../models/OrderHistoryMenu';
import OrderHistoryMenu from '../models/OrderHistoryMenu';

type Dispatch = (action: Action) => void;

type Action =
  | { type: 'toggleDetailsExpanded'; areDetailsExpanded: boolean }
  | { type: 'reload'; data: IOrderHistoryMenu };

type Props = {
  children: ReactNode,
  value: OrderHistoryMenu
 }

type ContextValue = {
  orderHistoryMenu: OrderHistoryMenu;
  dispatch: Dispatch;
};

const OrderHistoryContext = createContext<ContextValue | null>(null);

function OrderHistoryReducer(
  orderHistoryMenu: OrderHistoryMenu,
  action: Action,
): OrderHistoryMenu {
  switch (action.type) {
    case 'toggleDetailsExpanded': {
      return new OrderHistoryMenu({
        ...orderHistoryMenu,
        areDetailsExpanded: action.areDetailsExpanded,
      });
    }

    default: {
      return orderHistoryMenu;
    }
  }
}

function OrderHistoryProvider({ children, value }: Props): ReactElement {
  const [orderHistoryMenu, dispatch] = useReducer(
    OrderHistoryReducer,
    value,
  );

  return (
    <OrderHistoryContext.Provider value={{ orderHistoryMenu, dispatch }}>
      {children}
    </OrderHistoryContext.Provider>
  );
}

function useOrderHistory(): ContextValue {
  const context = useContext(OrderHistoryContext);

  if (context === null) {
    throw new Error(
      'useOrderHistory must be used within a OrderHistoryProvider',
    );
  }

  return context;
}

export { useOrderHistory, OrderHistoryProvider };
